import { API } from '@unboared/react';
import { DocumentReference, Firestore, Timestamp, addDoc, collection, updateDoc } from 'firebase/firestore';
import { normalizeScreenID } from '~/utils/screenID';
import { trackLivePlayers } from '~/utils/liveGamepads';

class GamepadSessionData {
    uid: string;

    startTime: Timestamp;

    /** Set once the session doc is created (after it crosses SECONDS_BEFORE_COUNT). */
    docRef?: DocumentReference;

    /** Guards against a double create when finalize + heartbeat race before docRef is set. */
    creating: boolean = false;

    /** A finalized segment is kept in the map (not deleted) so finalize stays idempotent,
     * the heartbeat and stop() skip it, and a genuine return (onConnect/onDeviceBack)
     * starts a fresh segment instead of resuming the saved one. */
    closed: boolean = false;

    constructor(uid: string, startTime: Timestamp) {
        this.uid = uid;
        this.startTime = startTime;
    }
}

export class FirestoreGamepadDataCollector implements IDataCollector {
    private static SECONDS_BEFORE_COUNT: number = 30;

    /** Upsert cadence — refreshes each live session's endTimestamp so an abrupt screen
     * close loses at most the last interval (~3 min) instead of the whole evening
     * (gotcha #111). Kept coarse on purpose: at a 74-player party a 60s cadence would be
     * ~74 writes/min for hours; 3 min keeps the recovery loss small at a fraction of the cost. */
    private static HEARTBEAT_MS: number = 180000;

    private _db: Firestore;

    private _clientID: string;

    private _gameID: string;

    private _screenID: string;

    private _liveTeardown?: () => void;

    private _gamepadsData: Map<string, GamepadSessionData> = new Map();

    private _heartbeatTimer?: ReturnType<typeof setInterval>;

    constructor(db: Firestore, clientID: string, gameID: string, screenID: string) {
        this._db = db;
        this._clientID = clientID;
        this._gameID = gameID;
        this._screenID = normalizeScreenID(screenID);
    }

    start() {
        // Shared seed/subscribe/reconcile lifecycle (cf gotcha #111/#103, same primitive
        // as useScreenPlayerCount and FirestoreScreenDataCollector, see liveGamepads.ts):
        // - onConnect: snappy registration of a new device.
        // - onDeviceBack: a device that reconnects within the grace period fires onDeviceBack,
        //   not onConnect (gotcha #2). Treat it as a fresh segment so its time keeps being
        //   recorded after the previous segment was saved on disconnect.
        // - onDisconnect: finalize promptly (before the device lingers as a grace ghost), which
        //   keeps the recorded duration accurate (the anim grace period is 6h).
        // - onReconcileNewDevice: seed + backstop INITIAL registration — right after a reload
        //   the device list is un-synced and already-connected gamepads never replay onConnect,
        //   so without it a full evening records nothing (gotcha #111). Its ever-seen semantics
        //   never re-deliver a device already delivered, so a finalized (closed) segment cannot
        //   be resurrected by mere lingering presence (phantom sessions) — reconnections go
        //   through onConnect/onDeviceBack, which re-register past a closed segment.
        this._liveTeardown = trackLivePlayers({
            onConnect: (deviceID: string) => this._registerGamepad(deviceID),
            onDeviceBack: (deviceID: string) => this._registerGamepad(deviceID),
            onDisconnect: (deviceID: string) => this._finalize(deviceID),
            onReconcileNewDevice: (deviceID: string) => this._registerGamepad(deviceID),
        });
        this._heartbeatTimer = setInterval(() => this._heartbeat(), FirestoreGamepadDataCollector.HEARTBEAT_MS);
    }

    private _registerGamepad(deviceID: string) {
        const existing = this._gamepadsData.get(deviceID);
        // Already tracking an open segment for this device → nothing to do.
        if (existing && !existing.closed) {
            return;
        }
        // New device, or one returning after a finalized segment → start a fresh segment.
        this._gamepadsData.set(deviceID, new GamepadSessionData(API.getUID(deviceID), Timestamp.now()));
    }

    /** Upsert each LIVE open segment's endTimestamp so progress survives an abrupt tab
     * close. Restricted to devices still in getGamepadIDs so a vanished-but-not-yet-finalized
     * segment is not rolled forward (reconcile finalizes it within RECONCILE_MS instead). */
    private _heartbeat() {
        const liveIDs = new Set(API.getGamepadIDs());
        this._gamepadsData.forEach((pdata, deviceID) => {
            if (!pdata.closed && liveIDs.has(deviceID)) {
                this._savePlayerSession(deviceID);
            }
        });
    }

    private _finalize(deviceID: string) {
        const pdata = this._gamepadsData.get(deviceID);
        if (!pdata || pdata.closed) {
            return;
        }
        pdata.closed = true;
        this._savePlayerSession(deviceID);
    }

    stop() {
        // Tear down reconcile interval + listeners. On a game switch this collector is
        // destroyed and a new one started; without unsubscribing, stale listeners keep firing
        // and re-save players against the previous gameID (inflated, cross-game durations) and leak.
        if (this._liveTeardown) {
            this._liveTeardown();
            this._liveTeardown = undefined;
        }
        if (this._heartbeatTimer) {
            clearInterval(this._heartbeatTimer);
            this._heartbeatTimer = undefined;
        }
        this._gamepadsData.forEach((pdata, deviceID) => {
            if (!pdata.closed) {
                this._finalize(deviceID);
            }
        });
        this._gamepadsData.clear();
    }

    /** Create the session doc once it crosses the min duration, then keep its
     * endTimestamp fresh on later calls. Fire-and-forget, idempotent per segment. */
    private async _savePlayerSession(deviceID: string) {
        const pdata = this._gamepadsData.get(deviceID);
        if (!pdata || pdata.creating) {
            return;
        }
        const endTimestamp = Timestamp.now();
        const duration = endTimestamp.seconds - pdata.startTime.seconds;
        if (duration <= FirestoreGamepadDataCollector.SECONDS_BEFORE_COUNT) {
            return;
        }
        // Late-resolved uid: getUID can return '' right after connect; backfill once known
        // so the session is not silently dropped from distinct-player counts (gotcha #35).
        if (!pdata.uid) {
            pdata.uid = API.getUID(deviceID) || pdata.uid;
        }
        if (pdata.docRef) {
            updateDoc(pdata.docRef, { endTimestamp }).catch((e) => console.error(e));
            return;
        }
        pdata.creating = true;
        try {
            pdata.docRef = await addDoc(
                this._collectionRef(),
                this._makeNewDocument(pdata.uid, pdata.startTime, endTimestamp)
            );
        } catch (e) {
            console.error(e);
        } finally {
            pdata.creating = false;
        }
    }

    private _collectionRef() {
        return collection(this._db, 'clients', this._clientID, 'gamepadSession');
    }

    private _makeNewDocument(uid: string, startTimestamp: Timestamp, endTimestamp: Timestamp) {
        return {
            startTimestamp,
            endTimestamp,
            uid,
            gameID: this._gameID,
            screenID: this._screenID,
        };
    }
}
